import * as XLSX from 'xlsx';
import { buildPendingModel } from './pendingExportModel';

/**
 * Build and download an .xlsx sheet for one company's pending invoices,
 * matching the ExcelforClaude template.
 *
 * @param {string} company  Company / user name (fallback title)
 * @param {Array}  invoices Pending invoice rows from admin-stats pending_list
 */
export function exportPendingExcel(company, invoices) {
  const model = buildPendingModel(company, invoices);
  if (!model) return;

  const { isGel, title, fileBase, today, rows, sumTotal, headers } = model;

  // Row 1: merged company title. Row 2: headers (col A left empty, like the template).
  const aoa = [[title], ['', ...headers]];

  const firstDataRow = 3; // Excel 1-indexed
  rows.forEach((r) => {
    if (isGel) {
      aoa.push([r.label, r.qty, r.unit, r.value]);
    } else {
      aoa.push([r.label, r.qty, r.unit, r.value, r.vat, r.total]);
    }
  });

  const lastDataRow = firstDataRow + rows.length - 1;
  const sumRow = lastDataRow + 1;
  aoa.push(isGel ? ['', '', '', ''] : ['', '', '', '', '', '']);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  const lastCol = isGel ? 3 : 5; // 0-indexed (D or F)
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }];

  // Report date to the right of the header
  ws[XLSX.utils.encode_cell({ r: 0, c: lastCol + 2 })] = { t: 's', v: today };

  // Replace computed cells with formulas (keeps cached values for non-Excel viewers)
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const B = `B${r}`, C = `C${r}`, D = `D${r}`, E = `E${r}`, F = `F${r}`;
    const dVal = ws[D] ? ws[D].v : 0;
    ws[D] = { t: 'n', f: `${B}*${C}`, v: dVal };
    if (!isGel) {
      const eVal = ws[E] ? ws[E].v : 0;
      const fVal = ws[F] ? ws[F].v : 0;
      ws[E] = { t: 'n', f: `${D}*0.2`, v: eVal };
      ws[F] = { t: 'n', f: `${D}+${E}`, v: fVal };
    }
  }

  // Sum of totals
  if (rows.length > 0) {
    const totalCol = isGel ? 'D' : 'F';
    ws[`${totalCol}${sumRow}`] = {
      t: 'n',
      f: `SUM(${totalCol}${firstDataRow}:${totalCol}${lastDataRow})`,
      v: sumTotal,
    };
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: sumRow - 1, c: lastCol + 2 } });
  }

  ws['!cols'] = isGel
    ? [{ wch: 45 }, { wch: 12 }, { wch: 14 }, { wch: 16 }]
    : [{ wch: 55 }, { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];

  const wb = XLSX.utils.book_new();
  const safeName = String(title || 'Sheet').replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Sheet';
  XLSX.utils.book_append_sheet(wb, ws, safeName);

  const filename = `${fileBase}_pending_${today}.xlsx`;
  XLSX.writeFile(wb, filename); // download

  // Also return the file as a Blob so callers can upload it (e.g. attach to a firm).
  const arr = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([arr], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return { blob, filename, isGel };
}
